import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import Header from "../components/app/Header";
import BusinessLogo from "../components/app/BusinessLogo";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Save,
  CheckCircle2,
  Circle,
  Loader2,
  ArrowRight,
  Lightbulb,
} from "lucide-react";
import { toast } from "sonner";

export default function KnowledgePage() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // question_id -> string
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/knowledge/questions"),
      api.get("/knowledge/answers"),
      api.get("/auth/me"),
    ])
      .then(([q, a, me]) => {
        setQuestions(q.data);
        setAnswers(a.data || {});
        setLogoUrl(me.data?.business_logo_url || null);
      })
      .catch(() => toast.error("Failed to load Knowledge Base"))
      .finally(() => setLoading(false));
  }, []);

  const answeredCount = useMemo(
    () =>
      questions.reduce(
        (n, q) => n + ((answers[q.id] || "").trim() ? 1 : 0),
        0,
      ),
    [questions, answers],
  );
  const progress = questions.length
    ? Math.round((answeredCount / questions.length) * 100)
    : 0;

  const setAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    setDirty(true);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Trim and send only what changed (simpler: send everything; backend handles empty=clear)
      const payload = {};
      questions.forEach((q) => {
        payload[q.id] = (answers[q.id] || "").trim();
      });
      const res = await api.post("/knowledge/answers", { answers: payload });
      const saved = res.data?.saved ?? 0;
      const cleared = res.data?.cleared ?? 0;
      toast.success(
        cleared > 0
          ? `Saved ${saved} answer${saved === 1 ? "" : "s"} (${cleared} cleared)`
          : `Saved ${saved} answer${saved === 1 ? "" : "s"}`,
      );
      setDirty(false);
    } catch {
      toast.error("Couldn't save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-8 mb-10 grain">
          <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative flex items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0 glow-primary">
              <BookOpen className="w-6 h-6 text-emerald-300" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-400 mb-2">
                // your knowledge base
              </div>
              <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-black leading-[1.05]">
                Tell every agent about your business — <span className="text-emerald-400">once</span>.
              </h1>
              <p className="mt-3 text-zinc-400 text-sm max-w-xl">
                The more you fill, the smarter your agents get. They'll
                automatically skip questions you've already answered here and
                jump straight to the strategic work.
              </p>

              {/* Progress */}
              <div className="mt-6 max-w-md">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  <span>Progress</span>
                  <span className="text-emerald-300" data-testid="kb-progress-text">
                    {answeredCount} / {questions.length} answered
                  </span>
                </div>
                <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                  <div
                    data-testid="kb-progress-bar"
                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tip strip */}
        <div className="flex items-start gap-3 mb-6 px-4 py-3 rounded-xl bg-amber-500/[0.05] border border-amber-500/20">
          <Lightbulb className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-300 leading-relaxed">
            Answers are saved on this device and visible only to <span className="text-white font-medium">you and your admin</span>.
            Every Custom GPT in the hub reads these before each chat — no more
            repeating yourself.
          </div>
        </div>

        {/* Business logo — used by branded tools like PostCraft */}
        <BusinessLogo logoUrl={logoUrl} onChange={setLogoUrl} />

        {/* Questions */}
        {loading ? (
          <div className="text-zinc-500 text-sm py-12 text-center">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading questions...
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-zinc-800 rounded-2xl">
            <BookOpen className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
            <div className="font-display text-xl tracking-tight">
              No questions configured yet
            </div>
            <div className="text-sm text-zinc-500 mt-1">
              Your admin will add questions soon.
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {questions.map((q, i) => {
              const value = answers[q.id] || "";
              const filled = value.trim().length > 0;
              return (
                <div
                  key={q.id}
                  data-testid={`kb-question-${q.id}`}
                  className={`rounded-2xl border bg-zinc-950/40 p-5 transition-colors ${
                    filled
                      ? "border-emerald-500/30"
                      : "border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    {filled ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-zinc-600 mt-1 shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                        Question {String(i + 1).padStart(2, "0")}
                      </div>
                      <h3 className="font-display text-lg tracking-tight font-bold text-white">
                        {q.prompt}
                      </h3>
                      {q.help_text && (
                        <p className="text-xs text-zinc-500 mt-1">{q.help_text}</p>
                      )}
                    </div>
                  </div>
                  <textarea
                    data-testid={`kb-answer-${q.id}`}
                    value={value}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    placeholder={q.placeholder || "Your answer..."}
                    rows={3}
                    className="w-full bg-zinc-900/60 border border-zinc-800 rounded-xl px-4 py-3 text-[14px] text-white outline-none focus:border-emerald-500/50 resize-y leading-relaxed"
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Sticky save bar */}
        {questions.length > 0 && (
          <div className="sticky bottom-4 mt-8 z-30">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-950/90 backdrop-blur p-4 flex items-center justify-between gap-4 shadow-xl">
              <div className="text-xs text-zinc-400">
                {dirty ? (
                  <span className="text-amber-300">Unsaved changes</span>
                ) : (
                  <span>All changes saved</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/dashboard"
                  data-testid="kb-back-to-dashboard"
                  className="text-xs uppercase tracking-[0.18em] text-zinc-400 hover:text-white px-3 py-2 rounded-lg border border-transparent hover:border-zinc-800 inline-flex items-center gap-1.5"
                >
                  Back to agents <ArrowRight className="w-3 h-3" />
                </Link>
                <button
                  data-testid="kb-save-btn"
                  onClick={save}
                  disabled={saving || !dirty}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-semibold rounded-xl px-5 py-2.5 transition-colors glow-primary"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
